/**
 * Collects rich device info from the browser's Navigator API (with safe fallbacks).
 * Returns a compact key-value string to keep request payload small.
 */
export function getDeviceInfo(): string {
  if (typeof navigator === 'undefined') {
    return 'ua=unknown; platform=unknown; lang=unknown; tz=unknown'
  }

  const nav = navigator as Navigator & {
    deviceMemory?: number
    userAgentData?: {
      brands?: Array<{ brand: string; version: string }>
      mobile?: boolean
      platform?: string
    }
  }

  const ua = nav.userAgent || 'unknown'
  const uaBrands =
    nav.userAgentData?.brands?.map(({ brand, version }) => `${brand}:${version}`).join(',') ?? null
  const uaPlatform = nav.userAgentData?.platform || null

  const platform = nav.platform || 'unknown'
  const language = nav.language || 'unknown'
  const languages = Array.isArray(nav.languages) ? nav.languages.join(',') : null
  const vendor = nav.vendor || null
  const hardwareConcurrency = typeof nav.hardwareConcurrency === 'number' ? nav.hardwareConcurrency : null
  const deviceMemory = typeof nav.deviceMemory === 'number' ? nav.deviceMemory : null
  const maxTouchPoints = typeof (nav as Navigator & { maxTouchPoints?: number }).maxTouchPoints === 'number'
    ? (nav as Navigator & { maxTouchPoints?: number }).maxTouchPoints
    : null

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown'
  const screenInfo =
    typeof screen !== 'undefined'
      ? `${screen.width}x${screen.height}`
      : null

  const parts = [
    `ua=${ua}`,
    uaBrands ? `uaBrands=${uaBrands}` : null,
    uaPlatform ? `uaPlat=${uaPlatform}` : null,
    `platform=${platform}`,
    `lang=${language}`,
    languages ? `langs=${languages}` : null,
    vendor ? `vendor=${vendor}` : null,
    hardwareConcurrency !== null ? `cores=${hardwareConcurrency}` : null,
    deviceMemory !== null ? `mem=${deviceMemory}GB` : null,
    maxTouchPoints !== null ? `touch=${maxTouchPoints}` : null,
    `tz=${timezone}`,
    screenInfo ? `screen=${screenInfo}` : null,
  ]

  return parts.filter(Boolean).join('; \n')
}
